import React, { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import Cookies from "js-cookie";
import { Row, Col, Form, Button, Pagination } from "react-bootstrap";
import { CiSearch } from "react-icons/ci";
import {
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { LuDownload } from "react-icons/lu";

const AttandenceTable = ({ columns = [], data = [] }) => {
  const { Colourdata } = useSelector((state) => state.ClientLogin);

  const iscolour = Colourdata?.data?.find((el) => el.ExeName === "NEWPAYROLL");
  const iscolourDark = Colourdata?.data?.find(
    (el) => el.ExeName === "NEWPAYROLLDARK",
  );

  const isdark = Cookies.get("theme") || "light";

  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState(null);
  const [sortType, setSortType] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);

  const rowsPerPage = 10;

  // 🔍 SEARCH
  const filteredData = useMemo(() => {
    return data.filter((row) =>
      Object.values(row).some((val) =>
        val?.toString().toLowerCase().includes(search.toLowerCase()),
      ),
    );
  }, [data, search]);

  // 🔃 SORT
  const sortedData = useMemo(() => {
    if (!sortKey) return filteredData;

    return [...filteredData].sort((a, b) => {
      let aVal = a[sortKey];
      let bVal = b[sortKey];

      if (!isNaN(aVal) && !isNaN(bVal)) {
        return sortType === "asc" ? aVal - bVal : bVal - aVal;
      }

      return sortType === "asc"
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [filteredData, sortKey, sortType]);

  // 📄 PAGINATION
  const indexOfLast = currentPage * rowsPerPage;
  const indexOfFirst = indexOfLast - rowsPerPage;
  const currentRows = sortedData.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  const visiblePages = 4;

  let startPage = Math.max(1, currentPage - Math.floor(visiblePages / 2));
  let endPage = startPage + visiblePages - 1;

  if (endPage > totalPages) {
    endPage = totalPages;
    startPage = Math.max(1, endPage - visiblePages + 1);
  }

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortType(sortType === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortType("asc");
    }
  };


  const downloadCSV = () => {
    if (!sortedData || sortedData.length === 0) return;

    // headers
    const headers = columns.map((col) => col.label);

    // rows
    const rows = sortedData.map((row) =>
      columns.map((col) => {
        let value = row[col.key];

        // commas handle karne ke liye quotes
        if (typeof value === "string" && value.includes(",")) {
          value = `"${value}"`;
        }

        return value ?? "";
      }),
    );

    // combine
    const csvContent = [headers, ...rows].map((e) => e.join(",")).join("\n");

    // file create
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    // download trigger
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "table_data.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      {/* STYLE */}
      <style>
        {`
.custom-pagination {
  gap: 8px;
}

.custom-pagination .page-item .page-link {
  border: none;
  border-radius: 8px;
  min-width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${isdark === "light" ? iscolour?.Colour4 : iscolourDark?.Colour6};
  color: ${isdark === "light" ? iscolour?.Colour11 : iscolourDark?.Colour3};
  font-weight: 500;
}

.custom-pagination .page-item.active .page-link {
  background: ${isdark === "light" ? iscolour?.Colour1 : iscolourDark?.Colour9};
}

.custom-pagination .page-item .page-link:hover {
  background: ${isdark === "light" ? iscolour?.Colour3 : iscolourDark?.Colour11
          };
}


`}
      </style>

      {/* TOP BAR */}
      <div
        style={{
          width: "100%",
          background:
            isdark === "light" ? iscolour?.Colour3 : iscolourDark?.Colour8,
        }}
      >
        <Row className="g-2 align-items-center mb-2 m-0 px-2 px-md-3">
          {/* Search */}
          <Col xs={12} md={5} lg={5} xl={4}>
            <div
              style={{
                position: "relative",
                width: "100%",
              }}
            >
              <CiSearch
                size={18}
                style={{
                  position: "absolute",
                  left: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color:
                    isdark === "light"
                      ? iscolour?.Colour11
                      : iscolourDark?.Colour12,
                  zIndex: 1,
                }}
              />

              <Form.Control
                type="text"
                placeholder="Search..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
                style={{
                  borderRadius: "8px",
                  height: "40px",
                  padding: "0 10px 0 35px",
                  border: `1px solid ${isdark === "light"
                    ? iscolour?.Colour4
                    : iscolourDark?.Colour3
                    }`,
                  backgroundColor:
                    isdark === "light"
                      ? iscolour?.Colour1
                      : iscolourDark?.Colour4,
                  color:
                    isdark === "light"
                      ? iscolour?.Colour11
                      : iscolourDark?.Colour12,
                }}
              />
            </div>
          </Col>

          {/* Download Button */}
          <Col
            xs={12}
            md={7}
            lg={7}
            xl={8}
            style={{ textAlign: "right" }}
          >
            <Button
              onClick={downloadCSV}
              className="hover-scale-btn"
              style={{
                background:
                  isdark === "light"
                    ? iscolour?.Colour6
                    : iscolourDark?.Colour1,
                color:
                  isdark === "light"
                    ? iscolour?.Colour1
                    : iscolourDark?.Colour10,
                border: "none",
                borderRadius: "8px",
                height: "40px",
                padding: "0 16px",
                fontSize: "14px",
                fontWeight: "600",
              }}
            >
              <LuDownload size={16} />
              <span className="ms-1">Download CSV</span>
            </Button>
          </Col>
        </Row>

        {/* TABLE */}
        <div
          style={{
            overflow: "auto",
            maxHeight: "400px",
            overflowX: "auto",
            overflowY: "auto",
            width: "100%",
            height: "400px",
            scrollbarWidth: "thin",
            scrollbarColor:
              isdark === "light"
                ? `${iscolour?.Colour5} ${iscolour?.Colour2}`
                : `${iscolourDark?.Colour10} ${iscolourDark?.Colour5}`,
          }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {columns.map((col, i) => (
                  <th
                    key={i}
                    onClick={() => handleSort(col.key)}
                    style={{
                      padding: "14px",
                      textAlign: "center",
                      fontSize: "13px",
                      fontWeight: "700",
                      cursor: "pointer",
                      position: "sticky",
                      top: 0,
                      zIndex: 2,
                      borderBottom: `2px solid ${isdark === "light"
                        ? iscolour?.Colour6
                        : iscolourDark?.Colour6
                        }`,
                      background:
                        isdark === "light"
                          ? iscolour?.Colour3
                          : iscolourDark?.Colour8,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      {col.label}

                      {sortKey === col.key ? (
                        sortType === "asc" ? (
                          <ArrowUp size={14} />
                        ) : (
                          <ArrowDown size={14} />
                        )
                      ) : (
                        <ArrowUpDown size={14} />
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {currentRows.map((row, i) => (
                <tr
                  key={i}
                  style={{

                    backgroundColor:
                      i % 2 === 0
                        ? isdark === "light"
                          ? iscolour?.Colour1
                          : iscolourDark?.Colour4
                        : isdark === "light"
                          ? iscolour?.Colour3
                          : iscolourDark?.Colour1,
                  }}
                >
                  {columns.map((col, j) => {
                    const value = row[col.key];

                    if (
                      ["A", "P", "D", "W", "L", "H", "N", "LT", "1/2"].includes(
                        value,
                      )
                    ) {
                      const colorMap = {
                        A: "#FF3B30",
                        P: "#4CAF50",
                        D: "#FFD740",
                        L: "#FF6F61",
                        H: "#40C4FF",
                        W: "#64B5F6",
                        N: "#7E57C2",
                        LT: "#FFA726",
                        "1/2": "#D7A86E",
                      };

                      return (
                        <td key={j} style={{ textAlign: "center" }}>
                          <div
                            style={{
                              minWidth: "24px",
                              height: "24px",
                              borderRadius: "14px",
                              backgroundColor: colorMap[value],
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontWeight: "700",
                              fontSize: "11px",
                              color: value === "A" ? "#fff" : "#000",
                            }}
                          >
                            {value}
                          </div>
                        </td>
                      );
                    }

                    return (
                      <td
                        key={j}
                        style={{
                          padding: "10px 12px",
                          fontSize: "13px",
                          fontWeight: "600",
                          color:
                            isdark === "light"
                              ? iscolour?.Colour12
                              : iscolourDark?.Colour12,
                        }}
                      >
                        {col.format ? col.format(value) : value}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* FOOTER */}
        <Row
          className="m-0 px-2"
          style={{
            marginTop: "10px",
            backgroundColor:
              isdark === "light" ? iscolour?.Colour6 : iscolourDark?.Colour7,
            padding: "12px 10px",
          }}
        >
          <Col md={6} style={{ fontSize: "12px" }}>
            Showing {indexOfFirst + 1} to{" "}
            {Math.min(indexOfLast, sortedData.length)} of {sortedData.length}
          </Col>

          <Col md={6} className="d-flex justify-content-end">
            <Pagination className="custom-pagination m-0">
              <Pagination.Item
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                <ChevronLeft size={14} />
              </Pagination.Item>

              {Array.from({ length: endPage - startPage + 1 }, (_, i) => {
                const page = startPage + i;
                return (
                  <Pagination.Item
                    key={page}
                    active={currentPage === page}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </Pagination.Item>
                );
              })}

              <Pagination.Item
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                <ChevronRight size={14} />
              </Pagination.Item>
            </Pagination>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default AttandenceTable;
